import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Token refresh tracking
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('equipr_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors with token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Check if this is a 401 error and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't try to refresh if this is a login/register/refresh request
      if (originalRequest.url?.includes('/auth/login') || 
          originalRequest.url?.includes('/auth/register') ||
          originalRequest.url?.includes('/auth/refresh')) {
        return Promise.reject(error);
      }
      
      if (isRefreshing) {
        // Queue this request until refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      const currentToken = localStorage.getItem('equipr_token');
      
      if (!currentToken) {
        // No token, go to login
        isRefreshing = false;
        localStorage.removeItem('equipr_user');
        window.location.href = '/login';
        return Promise.reject(error);
      }
      
      try {
        // Try to refresh the token
        const response = await axios.post(
          `${API_URL}/api/auth/refresh`,
          {},
          { headers: { Authorization: `Bearer ${currentToken}` } }
        );
        
        const { token: newToken, user } = response.data;
        
        // Store new token
        localStorage.setItem('equipr_token', newToken);
        localStorage.setItem('equipr_user', JSON.stringify(user));
        
        // Update default header
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        
        // Process queued requests with new token
        processQueue(null, newToken);
        
        // Retry original request
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
        
      } catch (refreshError) {
        // Token refresh failed - session is truly expired
        processQueue(refreshError, null);
        localStorage.removeItem('equipr_token');
        localStorage.removeItem('equipr_user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  refreshToken: () => api.post('/auth/refresh'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
  changePassword: (current_password, new_password) =>
    api.post('/auth/change-password', { current_password, new_password }),
  getSecurity: () => api.get('/auth/security'),
  revokeOtherSessions: () => api.post('/auth/sessions/revoke-others'),
  sessionTimeout: () => api.post('/auth/session-timeout'),
  securityActivity: (params) => api.get('/security/activity', { params }),
};

// Assets
export const assetsApi = {
  getAll: (params) => api.get('/assets', { params }),
  getById: (id) => api.get(`/assets/${id}`),
  getByQR: (qrData) => api.get(`/assets/qr/${encodeURIComponent(qrData)}`),
  create: (data) => api.post('/assets', data),
  update: (id, data) => api.put(`/assets/${id}`, data),
  getHistory: (id, params) => api.get(`/assets/${id}/history`, { params }),
  downloadFile: (path) => api.get(path, { responseType: 'blob' }),
  quarantine: (id, reason) => api.post(`/assets/${id}/quarantine`, { reason }),
  releaseQuarantine: (id) => api.delete(`/assets/${id}/quarantine`),
  bulkReadiness: (data) => api.post('/assets/bulk/readiness', data),
  bulkDepot: (asset_ids, depot_id) => api.post('/assets/bulk/depot', { asset_ids, depot_id }),
  delete: (id) => api.delete(`/assets/${id}`),
  regenerateQRCodes: () => api.post('/assets/regenerate-qr-codes'),
  // Phase 51b: print-ready QR sticker PDF. responseType:blob so we can
  // hand the bytes to a browser download without corruption. The backend
  // returns a 2×4 sticker grid per A4 page (8 per page by default).
  downloadQrStickerPdf: (id, copies = 8) =>
    api.get(`/assets/${id}/qr-sticker.pdf`, {
      params: { copies },
      responseType: 'blob',
    }),
};

// Inspection Templates
export const templatesApi = {
  getAll: () => api.get('/inspection-templates'),
  getById: (id) => api.get(`/inspection-templates/${id}`),
  create: (data) => api.post('/inspection-templates', data),
  update: (id, data) => api.put(`/inspection-templates/${id}`, data),
  publish: (id) => api.post(`/inspection-templates/${id}/publish`),
  delete: (id) => api.delete(`/inspection-templates/${id}`),
};

// Inspections
export const inspectionsApi = {
  getAll: (params) => api.get('/inspections', { params }),
  getById: (id) => api.get(`/inspections/${id}`),
  create: (data) => api.post('/inspections', data),
  delete: (id) => api.delete(`/inspections/${id}`),
  downloadPdf: (id) => api.get(`/inspections/${id}/pdf`, { responseType: 'blob' }),
};

// Phase 52: Inspection Templates + per-asset public-QR whitelist
export const inspectionTemplatesApi = {
  list: () => api.get('/inspection-templates'),
  getById: (id) => api.get(`/inspection-templates/${id}`),
  // Asset whitelist for public-QR scanners
  linkToAsset: (assetId, templateId) =>
    api.post(`/assets/${assetId}/public-inspection-templates/${templateId}`),
  unlinkFromAsset: (assetId, templateId) =>
    api.delete(`/assets/${assetId}/public-inspection-templates/${templateId}`),
};

// Defects
export const defectsApi = {
  getAll: (params) => api.get('/defects', { params }),
  getById: (id) => api.get(`/defects/${id}`),
  create: (data) => api.post('/defects', data),
  update: (id, data) => api.put(`/defects/${id}`, data),
  delete: (id) => api.delete(`/defects/${id}`),
};

// Work Orders
export const labourTypesApi = {
  getAll: () => api.get('/labour-types'),
  create: (data) => api.post('/labour-types', data),
  update: (id, data) => api.put(`/labour-types/${id}`, data),
  delete: (id) => api.delete(`/labour-types/${id}`),
};


export const workOrdersApi = {  getAll: (params) => api.get('/work-orders', { params }),
  getById: (id) => api.get(`/work-orders/${id}`),
  create: (data) => api.post('/work-orders', data),
  update: (id, data) => api.put(`/work-orders/${id}`, data),
  delete: (id) => api.delete(`/work-orders/${id}`),
  bulkDelete: (ids) => api.post('/work-orders/bulk-delete', { ids }),
  bulkAssign: (ids, assigned_to) => api.post('/work-orders/bulk-assign', { ids, assigned_to }),
  combine: (ids, primary_id, assigned_to) => api.post('/work-orders/combine', { ids, primary_id, assigned_to }),
  getOpenForAsset: (assetId) => api.get(`/work-orders/open-for-asset/${assetId}`),
  linkDefect: (workOrderId, defectId) => api.post(`/work-orders/${workOrderId}/link-defect/${defectId}`),
  linkMaintenance: (workOrderId, scheduleId) => api.post(`/work-orders/${workOrderId}/link-maintenance/${scheduleId}`),
  // Timer endpoints
  startTimer: (id) => api.post(`/work-orders/${id}/start-timer`),
  pauseTimer: (id) => api.post(`/work-orders/${id}/pause-timer`),
  resumeTimer: (id) => api.post(`/work-orders/${id}/resume-timer`),
  completeWithSignature: (id, data) => api.post(`/work-orders/${id}/complete-with-signature`, data),
  startEntry: (id) => api.post(`/work-orders/${id}/time-entries/start`),
  pauseEntry: (id, entryId) => api.post(`/work-orders/${id}/time-entries/${entryId}/pause`),
  addEntry: (id, data) => api.post(`/work-orders/${id}/time-entries`, data),
  editEntry: (id, entryId, data) => api.put(`/work-orders/${id}/time-entries/${entryId}`, data),
  deleteEntry: (id, entryId) => api.delete(`/work-orders/${id}/time-entries/${entryId}`),
  downloadPdf: (id) => api.get(`/work-orders/${id}/pdf`, { responseType: 'blob' }),
};

export const readinessApi = {
  getSettings: () => api.get('/readiness/settings'),
  putSettings: (data) => api.put('/readiness/settings', data),
  putConfig: (category, data) => api.put(`/readiness/config/${category}`, data),
  recompute: () => api.post('/readiness/recompute'),
  getSummary: () => api.get('/readiness/summary'),
};

export const inventoryApi = {
  listParts: (params) => api.get('/inventory/parts', { params }),
  getPart: (id) => api.get(`/inventory/parts/${id}`),
  lookup: (code) => api.get('/inventory/parts/lookup', { params: { code } }),
  createPart: (data) => api.post('/inventory/parts', data),
  updatePart: (id, data) => api.put(`/inventory/parts/${id}`, data),
  deletePart: (id) => api.delete(`/inventory/parts/${id}`),
  importParts: (rows) => api.post('/inventory/parts/import', rows),
  setStock: (id, depotId, data) => api.put(`/inventory/parts/${id}/stock/${depotId}`, data),
  adjust: (id, data) => api.post(`/inventory/parts/${id}/adjust`, data),
  transfer: (id, data) => api.post(`/inventory/parts/${id}/transfer`, data),
  alerts: () => api.get('/inventory/alerts'),
  listPurchaseOrders: () => api.get('/inventory/purchase-orders'),
  getPurchaseOrder: (id) => api.get(`/inventory/purchase-orders/${id}`),
  createPurchaseOrder: (data) => api.post('/inventory/purchase-orders', data),
  orderPurchaseOrder: (id) => api.post(`/inventory/purchase-orders/${id}/order`),
  receivePurchaseOrder: (id, data) => api.post(`/inventory/purchase-orders/${id}/receive`, data),
  reportValue: () => api.get('/inventory/reports/value'),
  reportUsage: (days) => api.get('/inventory/reports/usage', { params: { days } }),
};

export const vdaqApi = {
  status: () => api.get('/vdaq/status'),
  connect: (api_key) => api.post('/vdaq/connect', { api_key }),
  disconnect: () => api.post('/vdaq/disconnect'),
  vehicles: () => api.get('/vdaq/vehicles'),
  links: () => api.get('/vdaq/links'),
  link: (data) => api.post('/vdaq/link', data),
  unlink: (assetId) => api.delete(`/vdaq/link/${assetId}`),
  sync: () => api.post('/vdaq/sync'),
};

// Workshop Command Center (uses separate auth)
export const workshopApi = {
  auth: (clientId) => api.post('/workshop/auth', { client_id: clientId }),
  getDashboard: (workshopToken, depotId = null) => api.get('/workshop/dashboard', { 
    headers: { Authorization: `Bearer ${workshopToken}` },
    params: depotId ? { depot_id: depotId } : {}
  }),
  getDepots: (workshopToken) => api.get('/workshop/depots', {
    headers: { Authorization: `Bearer ${workshopToken}` }
  }),
};

// Maintenance Schedules
export const maintenanceApi = {
  getAll: (params) => api.get('/maintenance-schedules', { params }),
  getById: (id) => api.get(`/maintenance-schedules/${id}`),
  create: (data) => api.post('/maintenance-schedules', data),
  bulkCreate: (data) => api.post('/maintenance-schedules/bulk', data),
  update: (id, data) => api.put(`/maintenance-schedules/${id}`, data),
  complete: (id) => api.put(`/maintenance-schedules/${id}/complete`),
  bypass: (id, data) => api.post(`/maintenance-schedules/${id}/bypass`, data),
  pause: (id, data) => api.post(`/maintenance-schedules/${id}/pause`, data),
  resume: (id) => api.post(`/maintenance-schedules/${id}/resume`),
  setLeadTime: (id, data) => api.post(`/maintenance-schedules/${id}/lead-time`, data),
  delete: (id) => api.delete(`/maintenance-schedules/${id}`),
};

// Dashboard
export const dashboardApi = {
  getMetrics: () => api.get('/dashboard'),
};

// Reports
export const reportsApi = {
  getInspectionCompliance: (days = 30) => api.get('/reports/inspection-compliance', { params: { days } }),
  getDefectSummary: (days = 30) => api.get('/reports/defect-summary', { params: { days } }),
  getAssetHealth: () => api.get('/reports/asset-health'),
  getWorkOrderStatus: (days = 30) => api.get('/reports/work-order-status', { params: { days } }),
  getTeamActivity: (days = 30) => api.get('/reports/team-activity', { params: { days } }),
};

// Users
export const usersApi = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  invite: (data) => api.post('/users/invite', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  deactivate: (id) => api.post(`/users/${id}/deactivate`),
  reactivate: (id) => api.post(`/users/${id}/reactivate`),
  getReferences: (id) => api.get(`/users/${id}/references`),
  getSeatUsage: () => api.get('/users/seat-usage'),
  // Pending invites
  getPendingInvites: () => api.get('/users/invites/pending'),
  resendInvite: (inviteId) => api.post(`/users/invites/${inviteId}/resend`),
  cancelInvite: (inviteId) => api.delete(`/users/invites/${inviteId}`),
};

// Tenant Settings
export const tenantSettingsApi = {
  getIntegrations: () => api.get('/tenant/integration-settings'),
  updateIntegrations: (data) => api.put('/tenant/integration-settings', data),
  getCompany: () => api.get('/tenant/company'),
  updateCompany: (data) => api.put('/tenant/company', data),
  getStorage: () => api.get('/tenant/storage'),
  getPublicQR: () => api.get('/tenant-settings/public-qr'),
  updatePublicQR: (data) => api.put('/tenant-settings/public-qr', data),
  getPlan: () => api.get('/tenant-settings/plan'),
  getPlanUsage: () => api.get('/tenant-settings/plan/usage'),
  getDemoData: () => api.get('/tenant-settings/demo-data'),
  removeDemoData: () => api.delete('/tenant-settings/demo-data'),
  getRegional: () => api.get('/tenant-settings/regional'),
  setRegional: (data) => api.put('/tenant-settings/regional', data),
};

export const assetsImportApi = {
  getTemplate: () => api.get('/assets/import/template'),
  // `source` is optional — set to 'allocate' for Allocate CSV imports so the
  // backend matches by external_id first and updates in place instead of
  // skipping duplicates.
  preview: (rows, source = null, overwrite = false) => api.post('/assets/import/preview', { rows, source, overwrite }),
  importRows: (rows, source = null, overwrite = false) => api.post('/assets/import', { rows, source, overwrite }),
};

export const allocateApi = {
  info: () => api.get('/integrations/allocate/info'),
  enable: () => api.post('/integrations/allocate/enable'),
  disable: () => api.post('/integrations/allocate/disable'),
  // Two-part contract (Allocate emits): /external/allocate/{client_id}/{allocate_id}
  // The single-arg form is kept for backwards compatibility with the original Phase 34 URLs.
  resolve: (clientIdOrAllocateId, allocateId = null) =>
    allocateId === null
      ? api.get(`/external/allocate/${encodeURIComponent(clientIdOrAllocateId)}`)
      : api.get(`/external/allocate/${encodeURIComponent(clientIdOrAllocateId)}/${encodeURIComponent(allocateId)}`),
};

// Live SQL import from Allocate (Pro + Admin gated).
export const allocateSqlApi = {
  getConfig: () => api.get('/integrations/allocate/sql/config'),
  saveConfig: (body) => api.put('/integrations/allocate/sql/config', body),
  clearConfig: () => api.put('/integrations/allocate/sql/config', { clear: true }),
  testConnection: (body) => api.post('/integrations/allocate/sql/test', body),
  preview: (body) => api.post('/integrations/allocate/sql/preview', body),
  commit: (body) => api.post('/integrations/allocate/sql/import', body),
  previewSaved: (changed_since = null, force_resync = false) =>
    api.post('/integrations/allocate/sql/preview-saved', { changed_since, force_resync }),
  commitSaved: (changed_since = null, force_resync = false) =>
    api.post('/integrations/allocate/sql/import-saved', { changed_since, force_resync }),
};

// Public API (no auth)
const publicAxios = axios.create({ baseURL: `${API_URL}/api/public` });

export const publicApi = {
  getAsset: (code) => publicAxios.get(`/asset/${code}`),
  reportIssue: (code, data) => publicAxios.post(`/asset/${code}/report`, data),
  // Phase 51: anonymous Error Code lookup.
  lookupErrorCode: (qrCode, errorCode) =>
    publicAxios.get(`/asset/${qrCode}/error-code/${encodeURIComponent(errorCode)}`),
  // Phase 52: list templates the admin has whitelisted for public scanning,
  // and submit a completed inspection. Same anonymous auth posture as report.
  listInspectionTemplates: (code) =>
    publicAxios.get(`/asset/${code}/inspection-templates`),
  submitInspection: (code, data) =>
    publicAxios.post(`/asset/${code}/inspection`, data),
  // Phase 50: file URLs for direct <a href> links.
  documentFileUrl: (code, docId) =>
    `${publicAxios.defaults.baseURL}/asset/${code}/document/${docId}/file`,
  certificateFileUrl: (code, certId) =>
    `${publicAxios.defaults.baseURL}/asset/${code}/certificate/${certId}/file`,
};

// Error Code Libraries (Phase 51) — OEM service-manual PDFs uploaded by
// admins; the backend extracts every fault code into a structured library
// via Gemini 2.5 Flash. Each library can then be linked to assets so the
// public QR + Operator app can look codes up in the field.
export const errorCodeLibrariesApi = {
  list: () => api.get('/error-code-libraries'),
  getById: (id) => api.get(`/error-code-libraries/${id}`),
  // body: { name, make, model, file_data (data URL or bare base64), filename }
  create: (body) => api.post('/error-code-libraries', body),
  // codes belonging to one library, paginated server-side
  listCodes: (id, params) => api.get(`/error-code-libraries/${id}/codes`, { params }),
  delete: (id) => api.delete(`/error-code-libraries/${id}`),
  // Asset link/unlink — paths live on the asset_link_router which mounts
  // under /api directly, so the URL is /assets/{id}/error-code-libraries/{lib}
  linkToAsset: (assetId, libraryId) =>
    api.post(`/assets/${assetId}/error-code-libraries/${libraryId}`),
  unlinkFromAsset: (assetId, libraryId) =>
    api.delete(`/assets/${assetId}/error-code-libraries/${libraryId}`),
};


// Documents
export const documentsApi = {
  getAll: (params) => api.get('/documents', { params }),
  getById: (id) => api.get(`/documents/${id}`),
  upload: (data) => api.post('/documents', data),
  uploadVersion: (id, data) => api.post(`/documents/${id}/versions`, data),
  delete: (id) => api.delete(`/documents/${id}`),
  // Phase 50: toggle public visibility (and rename) per document.
  patch: (id, body) => api.patch(`/documents/${id}`, body),
};

// Lookups (Data Management)
export const lookupsApi = {
  getByCategory: (category, includeInactive = false) => 
    api.get(`/lookups/${category}`, { params: { include_inactive: includeInactive } }),
  create: (category, data) => api.post(`/lookups/${category}`, data),
  update: (category, id, data) => api.put(`/lookups/${category}/${id}`, data),
  delete: (category, id) => api.delete(`/lookups/${category}/${id}`),
  reorder: (category, items) => api.post(`/lookups/${category}/reorder`, items),
  getUsage: (category) => api.get(`/lookups/${category}/usage`),
  getAllCategories: () => api.get('/lookups'),
};

// Equipment Makes & Models
export const equipmentApi = {
  getMakes: (includeInactive = false) => 
    api.get('/equipment-makes', { params: { include_inactive: includeInactive } }),
  createMake: (data) => api.post('/equipment-makes', data),
  updateMake: (id, data) => api.put(`/equipment-makes/${id}`, data),
  deleteMake: (id) => api.delete(`/equipment-makes/${id}`),
  getModels: (makeId = null, includeInactive = false) => 
    api.get('/equipment-models', { params: { make_id: makeId, include_inactive: includeInactive } }),
  createModel: (data) => api.post('/equipment-models', data),
  updateModel: (id, data) => api.put(`/equipment-models/${id}`, data),
  deleteModel: (id) => api.delete(`/equipment-models/${id}`),
  makesUsage: () => api.get('/lookups/equipment/makes-usage'),
  modelsUsage: () => api.get('/lookups/equipment/models-usage'),
};

// Offline Sync
export const syncApi = {
  syncInspections: (data) => api.post('/sync/inspections', data),
  getStatus: () => api.get('/sync/status'),
};

// Notifications
export const notificationsApi = {
  getSettings: () => api.get('/notifications/settings'),
  updateSettings: (data) => api.put('/notifications/settings', data),
  getPending: () => api.get('/notifications/pending'),
  checkMaintenance: () => api.post('/notifications/check-maintenance'),
  sendTestEmail: (data) => api.post('/notifications/test-email', data),
  getMyPreferences: () => api.get('/notifications/my-preferences'),
  putMyPreferences: (data) => api.put('/notifications/my-preferences', data),
};

// In-App Notifications
export const inAppNotificationsApi = {
  getAll: (unreadOnly = false, limit = 50) => 
    api.get('/notifications', { params: { unread_only: unreadOnly, limit } }),
  getGrouped: () => api.get('/notifications/grouped'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
  acknowledge: (id) => api.put(`/notifications/${id}/acknowledge`),
  delete: (id) => api.delete(`/notifications/${id}`),
  generate: () => api.post('/notifications/generate'),
};

export const integrationApi = {
  createApiKey: (data) => api.post('/integration/api-keys', data),
  listApiKeys: () => api.get('/integration/api-keys'),
  revokeApiKey: (keyId) => api.delete(`/integration/api-keys/${keyId}`),
};

export const depotsApi = {
  list: () => api.get('/depots'),
  get: (id) => api.get(`/depots/${id}`),
  create: (data) => api.post('/depots', data),
  update: (id, data) => api.put(`/depots/${id}`, data),
  delete: (id) => api.delete(`/depots/${id}`),
  usage: () => api.get('/depots/usage/counts'),
};

export const webhooksApi = {
  list: () => api.get('/webhooks'),
  listEvents: () => api.get('/webhooks/events'),
  create: (data) => api.post('/webhooks', data),
  update: (id, data) => api.put(`/webhooks/${id}`, data),
  delete: (id) => api.delete(`/webhooks/${id}`),
  test: (id) => api.post(`/webhooks/${id}/test`),
};

export const assetLocationApi = {
  getHistory: (assetId) => api.get(`/assets/${assetId}/location-history`),
  moveToDepot: (assetId, depotId, notes) => 
    api.post(`/assets/${assetId}/move-depot?to_depot_id=${depotId}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}`),
};

export const userDepotsApi = {
  update: (userId, depotIds) => api.put(`/users/${userId}/depots`, depotIds),
};

// ─── Certificates API ────────────────────────────────────────────────
export const certificatesApi = {
  list: (params) => api.get('/certificates', { params }),
  get: (id) => api.get(`/certificates/${id}`),
  create: (data) => api.post('/certificates', data),
  update: (id, data) => api.put(`/certificates/${id}`, data),
  delete: (id) => api.delete(`/certificates/${id}`),
  expiringSummary: () => api.get('/certificates/expiring/summary'),
  supersede: (id, replacedById) => api.post(`/certificates/${id}/supersede`, { replaced_by_id: replacedById }),
  unsupersede: (id) => api.post(`/certificates/${id}/unsupersede`),
  // Phase 49: file attachment API
  uploadFile: (id, fileData, fileName, contentType) =>
    api.post(`/certificates/${id}/upload-file`, {
      file_data: fileData,
      file_name: fileName,
      content_type: contentType,
    }),
  deleteFile: (id) => api.delete(`/certificates/${id}/file`),
  // Fetch the file as a blob (with auth) — the caller can convert it into
  // an object URL and open in a new tab. We can't naively expose the raw
  // /file endpoint URL because the browser won't send the Authorization
  // header on a plain window.open().
  downloadFile: (id) => api.get(`/certificates/${id}/file`, {
    responseType: 'blob',
    // Tell axios NOT to treat redirects to Azure Blob signed URLs as
    // errors — the Azure URL itself is presigned + public-readable so
    // following the redirect is safe and removes a server hop.
    maxRedirects: 5,
  }),
};

// ─── Compliance Standards API ────────────────────────────────────────
export const complianceStandardsApi = {
  list: () => api.get('/compliance-standards'),
  get: (id) => api.get(`/compliance-standards/${id}`),
  create: (data) => api.post('/compliance-standards', data),
  update: (id, data) => api.put(`/compliance-standards/${id}`, data),
  delete: (id) => api.delete(`/compliance-standards/${id}`),
  seed: () => api.post('/compliance-standards/seed'),
  forCategory: (category) => api.get(`/compliance-standards/for-category/${category}`),
  // International Standards Library
  listLibrary: (params) => api.get('/compliance-standards/library/list', { params }),
  importFromLibrary: (libraryId) =>
    api.post(`/compliance-standards/library/${libraryId}/import`),
  // Asset linkage + compliance view
  linkToAsset: (assetId, standardId) =>
    api.post(`/compliance-standards/assets/${assetId}/standards/${standardId}`),
  unlinkFromAsset: (assetId, standardId) =>
    api.delete(`/compliance-standards/assets/${assetId}/standards/${standardId}`),
  assetCompliance: (assetId) =>
    api.get(`/compliance-standards/assets/${assetId}/compliance`),
  dashboardOverview: () =>
    api.get('/compliance-standards/dashboard/overview'),
  generateTemplate: (standardId, body) =>
    api.post(`/compliance-standards/${standardId}/generate-template`, body || {}),
};

// ─── Components API ──────────────────────────────────────────────────
export const componentsApi = {
  list: (params) => api.get('/components', { params }),
  get: (id) => api.get(`/components/${id}`),
  create: (data) => api.post('/components', data),
  update: (id, data) => api.put(`/components/${id}`, data),
  delete: (id) => api.delete(`/components/${id}`),
  transfer: (id, data) => api.post(`/components/${id}/transfer`, data),
  transfers: (id) => api.get(`/components/${id}/transfers`),
};

// ─── Work Order Costs API ────────────────────────────────────────────
export const workOrderCostsApi = {
  getSummary: (woId) => api.get(`/work-orders/${woId}/costs`),
  addItem: (woId, data) => api.post(`/work-orders/${woId}/costs`, data),
  editItem: (woId, itemId, data) => api.put(`/work-orders/${woId}/costs/${itemId}`, data),
  deleteItem: (woId, itemId) => api.delete(`/work-orders/${woId}/costs/${itemId}`),
};

// ─── Telemetry API ───────────────────────────────────────────────────
export const telemetryApi = {
  getConfig: (assetId) => api.get(`/telemetry/config/${assetId}`),
  updateConfig: (assetId, data) => api.put(`/telemetry/config/${assetId}`, data),
  getReadings: (assetId, limit = 100) => api.get(`/telemetry/readings/${assetId}`, { params: { limit } }),
  getLastPositions: () => api.get('/telemetry/last-positions'),
  registerImei: (assetId, imei) =>
    api.put(`/integrations/telematics/register/${assetId}`, { imei }),
  unregisterImei: (assetId) =>
    api.delete(`/integrations/telematics/register/${assetId}`),
  imeiStatus: (imei) =>
    api.get(`/integrations/telematics/imei/${imei}/status`),
  setSilenceMute: (assetId, muted) =>
    api.patch(`/telemetry/silence-mute/${assetId}`, { muted }),
  rotateToken: (assetId) => api.post(`/telemetry/config/${assetId}/rotate-token`),
};

// ─── Geofence API ────────────────────────────────────────────────────
export const geofencesApi = {
  list: () => api.get('/geofences'),
  create: (data) => api.post('/geofences', data),
  update: (id, data) => api.put(`/geofences/${id}`, data),
  delete: (id) => api.delete(`/geofences/${id}`),
  events: (params) => api.get('/geofences/events', { params }),
};

// ─── Insights API ────────────────────────────────────────────────────
export const insightsApi = {
  assetInsights: (assetId, months = 6) => api.get(`/insights/asset/${assetId}`, { params: { months } }),
  workOrderInsights: (months = 3) => api.get('/insights/work-orders', { params: { months } }),
};

// ─── Task Library API ────────────────────────────────────────────────
export const taskLibraryApi = {
  list: (params) => api.get('/task-library', { params }),
  get: (id) => api.get(`/task-library/${id}`),
  create: (data) => api.post('/task-library', data),
  update: (id, data) => api.put(`/task-library/${id}`, data),
  delete: (id) => api.delete(`/task-library/${id}`),
  categories: () => api.get('/task-library/categories'),
  suggestForAsset: (assetId) => api.get(`/task-library/suggest/${assetId}`),
};

// ─── Help & Support API ──────────────────────────────────────────────
export const helpApi = {
  listArticles: (params) => api.get('/help/articles', { params }),
  getArticle: (slug) => api.get(`/help/articles/${slug}`),
  categories: () => api.get('/help/categories'),
  askAI: (question) => api.post('/help/ai', { question }),
};

export const supportApi = {
  createTicket: (data) => api.post('/support/tickets', data),
  listMyTickets: () => api.get('/support/tickets'),
};

// ─── Work Order Tasks API ────────────────────────────────────────────
export const workOrderTasksApi = {
  getTasks: (woId) => api.get(`/work-orders/${woId}/tasks`),
  addTask: (woId, data) => api.post(`/work-orders/${woId}/tasks`, data),
  updateTask: (woId, taskId, data) => api.put(`/work-orders/${woId}/tasks/${taskId}`, data),
  deleteTask: (woId, taskId) => api.delete(`/work-orders/${woId}/tasks/${taskId}`),
  applyTemplate: (woId, templateId) => api.post(`/work-orders/${woId}/tasks/apply-template`, { template_id: templateId }),
};

// ─── Mobile Workshop Mode API ────────────────────────────────────────
const mobileAxios = axios.create({ baseURL: `${API_URL}/api/mobile` });

const mobileAuthHeader = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

export const mobileApi = {
  // Auth
  login: (data) => mobileAxios.post('/auth/login', data),
  setupPin: (pin, token) => mobileAxios.post('/auth/pin/setup', { pin }, mobileAuthHeader(token)),
  verifyPin: (pin, token) => mobileAxios.post('/auth/pin/verify', { pin }, mobileAuthHeader(token)),

  // Dashboard
  getDashboard: (token) => mobileAxios.get('/dashboard', mobileAuthHeader(token)),

  // Work Orders
  getWorkOrders: (token, status = 'active', limit = 20, offset = 0) => mobileAxios.get(`/work-orders?status=${status}&limit=${limit}&offset=${offset}`, mobileAuthHeader(token)),
  getWorkOrder: (id, token) => mobileAxios.get(`/work-orders/${id}`, mobileAuthHeader(token)),
  startWorkOrderTimer: (id, token) => mobileAxios.post(`/work-orders/${id}/start`, {}, mobileAuthHeader(token)),
  pauseWorkOrderTimer: (id, token) => mobileAxios.post(`/work-orders/${id}/pause`, {}, mobileAuthHeader(token)),
  completeWorkOrder: (id, data, token) => mobileAxios.post(`/work-orders/${id}/complete`, data, mobileAuthHeader(token)),
  toggleTask: (woId, taskId, data, token) => mobileAxios.put(`/work-orders/${woId}/tasks/${taskId}`, data, mobileAuthHeader(token)),
  addTask: (woId, data, token) => mobileAxios.post(`/work-orders/${woId}/tasks`, data, mobileAuthHeader(token)),

  // Inspections
  getInspectionsDue: (token) => mobileAxios.get('/inspections/due', mobileAuthHeader(token)),
  getInspectionHistory: (token, limit = 20, offset = 0) => mobileAxios.get(`/inspections/history?limit=${limit}&offset=${offset}`, mobileAuthHeader(token)),
  getInspectionTemplates: (token) => mobileAxios.get('/inspection-templates', mobileAuthHeader(token)),
  getInspectionTemplate: (id, token) => mobileAxios.get(`/inspection-templates/${id}`, mobileAuthHeader(token)),
  submitInspection: (data, token) => mobileAxios.post('/inspections', data, mobileAuthHeader(token)),

  // Maintenance (services due + self-allocate)
  getMaintenanceDue: (token) => mobileAxios.get('/maintenance/due', mobileAuthHeader(token)),
  claimMaintenance: (scheduleId, token) => mobileAxios.post(`/maintenance/${scheduleId}/claim`, {}, mobileAuthHeader(token)),

  // Defects
  getDefects: (token, filter = 'all', q = '', limit = 20, offset = 0) => mobileAxios.get(`/defects?filter=${filter}&q=${encodeURIComponent(q)}&limit=${limit}&offset=${offset}`, mobileAuthHeader(token)),
  getDefect: (id, token) => mobileAxios.get(`/defects/${id}`, mobileAuthHeader(token)),
  addDefectNote: (id, note, token) => mobileAxios.post(`/defects/${id}/note`, { note }, mobileAuthHeader(token)),
  resolveDefect: (id, token) => mobileAxios.post(`/defects/${id}/resolve`, {}, mobileAuthHeader(token)),
  raiseDefectWorkOrder: (id, token) => mobileAxios.post(`/defects/${id}/work-order`, {}, mobileAuthHeader(token)),
  reportDefect: (data, token) => mobileAxios.post('/defects', data, mobileAuthHeader(token)),

  // Assets
  getAsset: (id, token) => mobileAxios.get(`/assets/${id}`, mobileAuthHeader(token)),
  saveAssetReading: (id, hours, token) => mobileAxios.post(`/assets/${id}/reading`, { hours }, mobileAuthHeader(token)),
  getAssetPosition: (id, token) => mobileAxios.get(`/assets/${id}/position`, mobileAuthHeader(token)),
  raiseAssetWorkOrder: (id, token) => mobileAxios.post(`/assets/${id}/work-order`, {}, mobileAuthHeader(token)),
  searchAssets: (q, token) => mobileAxios.get(`/assets?q=${encodeURIComponent(q)}`, mobileAuthHeader(token)),

  // Inventory (field parts)
  searchParts: (q, token) => mobileAxios.get(`/parts?q=${encodeURIComponent(q)}`, mobileAuthHeader(token)),
  lookupPart: (code, token) => mobileAxios.get(`/parts/lookup?code=${encodeURIComponent(code)}`, mobileAuthHeader(token)),
  addWorkOrderPart: (woId, data, token) => mobileAxios.post(`/work-orders/${woId}/parts`, data, mobileAuthHeader(token)),

  // Profile
  getProfile: (token) => mobileAxios.get('/profile', mobileAuthHeader(token)),
};

// ─── Billing API ─────────────────────────────────────────────────────
export const billingApi = {
  me: () => api.get('/billing/me'),
  checkout: (originUrl, intent = {}) => api.post('/billing/checkout', {
    origin_url: originUrl,
    intent_office_users: intent.officeUsers ?? null,
    intent_gps_devices:  intent.gpsDevices  ?? null,
  }),
  portal: (originUrl) => api.post('/billing/portal', { origin_url: originUrl }),
  checkoutStatus: (sessionId) => api.get(`/billing/checkout/status/${sessionId}`),
};

export default api;
